import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Button, Accordion } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { adminApi } from '../../api';

import AccordionItem from '../common/AccordionItem';
import EndUserForm from '../common/endusers/EndUserForm';
import ConfirmationModal from '../common/ConfirmationModal';
import CopyButton from '../common/CopyButton';
import EndUserPassportMigrationModal from './EndUserPassportMigrationModal';

import { shallowCompareObjects, formatDateAndTime, generateFilterDate } from '../../utils';

import { useAlerts } from '../context';

function EndUserProfile({ endUser, getEndUser }) {
  const { t } = useTranslation();
  const [confirm, setConfirm] = useState({ show: false, data: undefined });
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [statusOnly, setStatusOnly] = useState(false);
  const formRef = useRef(null);
  const { setAlert } = useAlerts();
  const { handle, memberships, entity_type, apps } = endUser;
  const [expanded, setExpanded] = useState([1]);
  const handleExpanded = (key) =>
    setExpanded(expanded.includes(key) ? expanded.filter((e) => e !== key) : [...expanded, key]);
  const accordionActiveKey = (key) => expanded.includes(key) && key;

  const cancelUpdate = () => {
    setConfirm({ ...confirm, show: false });
    formRef.current.setSubmitting(false);
    formRef.current.resetForm({ values: endUser });
  };

  const updateUser = async (values) => {
    const diff = shallowCompareObjects(values, endUser);
    if (diff.first_name || diff.last_name) {
      const firstName = diff.first_name ?? values.first_name ?? '';
      const lastName = diff.last_name ?? values.last_name ?? '';
      diff.full_name = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ');
    }
    if (values) {
      try {
        await adminApi.updateEndUser(handle, diff);
        getEndUser();
        setAlert({
          message: t(`admin.user.update.message`, {
            name: diff.full_name || endUser.full_name || 'User',
          }),
          variant: 'success',
        });
      } catch (error) {
        setAlert({ message: error, variant: 'warning' });
        formRef.current.resetForm({ values: endUser });
      } finally {
        setConfirm({ ...confirm, show: false, data: undefined });
      }
    } else {
      formRef.current.setSubmitting(false);
      setConfirm({ ...confirm, show: false, data: undefined });
    }
  };

  return (
    <>
      <EndUserForm
        formRef={formRef}
        initialValues={endUser}
        onSubmit={(values) => setConfirm({ ...confirm, show: true, data: values })}
      />

      {memberships && memberships.length !== 0 && (
        <>
          <h4 className="mb-3">
            {t(
              `admin.endUsers.profile.headers.${
                entity_type === 'business' ? 'members' : 'memberships'
              }`
            )}
          </h4>
          <Card className="mb-5 border overflow-hidden">
            <Table responsive>
              <thead>
                <tr>
                  <th className="border-end">{t('common:form.fields.handle.label')}</th>
                  {memberships.some((membership) => membership.certification_token) && (
                    <th className="border-end">{t('common:kyb.certification_status')}</th>
                  )}
                  <th className="border-end">{t('common:kyb.roles.controlling_officer')}</th>
                  <th className="border-end">{t('common:kyb.roles.beneficial_owner')}</th>
                  <th>{t('common:kyb.roles.admin')}</th>
                </tr>
              </thead>
              <tbody>
                {memberships.map((membership, index) => (
                  <tr key={membership.business_handle || membership.user_handle + index}>
                    <td className="border-end">
                      <Button
                        href={`/admin/end_user/${
                          membership.business_handle || membership.user_handle
                        }/profile`}
                        variant="link"
                        className="text-reset text-underline p-0"
                      >
                        <span className="w-100 d-block text-break pe-3 text-start">
                          {membership.entity_name || membership.user_handle}
                        </span>
                      </Button>
                    </td>
                    {memberships.some((m) => m.certification_token) && (
                      <th
                        className="border-end"
                        style={index === memberships.length - 1 ? { borderBottom: 0 } : {}}
                      >
                        {membership.certification_token
                          ? t(
                              `common:kyb.${
                                membership.certification_token ? 'certified' : 'not_certified'
                              }`
                            )
                          : ''}
                      </th>
                    )}
                    <td className="border-end">
                      {membership.role === 'controlling_officer' && (
                        <i className="fas fa-circle text-primary" />
                      )}
                    </td>
                    <td className="border-end">
                      {membership.role === 'beneficial_owner' && (
                        <i className="fas fa-circle text-primary" />
                      )}
                    </td>
                    <td>
                      {membership.role === 'administrator' && (
                        <i className="fas fa-circle text-primary" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      <Accordion id="end_user_details" className="mb-4 pt-3" activeKey={accordionActiveKey(1)}>
        <AccordionItem
          className="border rounded"
          eventKey={1}
          label={t('admin.endUsers.profile.headers.end_user_details')}
          expanded={expanded}
          onSetExpanded={handleExpanded}
        >
          <div className="m-n4">
            <Table responsive className="custom-detail twoCol">
              <thead>
                <tr>
                  <th className="border-end">{t('common:labels.detail')}</th>
                  <th>{t('common:labels.data')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-end">{t('common:transactions.title')}</td>
                  <td>
                    <Button
                      href={`/admin/transactions?user_handle=${
                        endUser.handle
                      }&created=${generateFilterDate(
                        new Date(),
                        -30,
                        true
                      )}&created=${generateFilterDate(new Date())}`}
                      variant="link"
                      className="p-0 text-reset text-underline"
                    >
                      {t('common:transactions.past30Days')}
                    </Button>
                  </td>
                </tr>
                <tr className="border-bottom">
                  <td className="border-end">{t('admin.endUsers.profile.headers.sila_id')}</td>
                  <td>
                    {endUser.sila_id}
                    {endUser.sila_id && (
                      <CopyButton
                        textToCopy={endUser.sila_id}
                        variant="link"
                        placement="top"
                        className="p-1 ms-2"
                      />
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="border-end">
                    {t('admin.endUsers.profile.headers.priorityOnboarded')}
                  </td>
                  <td>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        {endUser.onboarded_at_priority
                          ? t('common:status.true')
                          : t('common:status.false')}
                      </div>
                      {endUser.onboarded_at_priority && (
                        <Button
                          onClick={() => {
                            setStatusOnly(true);
                            setShowMigrationModal(true);
                          }}
                          className="generate me-3 rounded-1 py-1"
                          variant="primary"
                          size="sm"
                        >
                          {t('admin.priorityMigration.buttons.migrationStatus')}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="border-end">
                    {t('admin.endUsers.profile.headers.priorityMigrated')}
                  </td>
                  <td>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        {endUser.migrated_to_priority
                          ? formatDateAndTime(endUser.migrated_to_priority)
                          : t('common:status.false')}
                      </div>
                      {!endUser.onboarded_at_priority && (
                        <Button
                          onClick={() => setShowMigrationModal(true)}
                          className="generate me-3 rounded-1 py-1"
                          variant="primary"
                          size="sm"
                        >
                          {t('admin.priorityMigration.buttons.migrateEntity')}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </Table>
            {apps && apps.length !== 0 && (
              <>
                <h4 className="border-top border-bottom p-3 m-0">
                  {t('admin.endUsers.profile.headers.apps')}
                </h4>
                <Table responsive>
                  <thead>
                    <tr>
                      <th className="border-end">
                        {t('applications.detail.form.fields.name.label')}
                      </th>
                      <th className="border-end">
                        {t('applications.detail.form.fields.handle.label')}
                      </th>
                      <th>{t('common:labels.created')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map((app) => (
                      <tr key={`${app.id}app_id`}>
                        <td className="border-end">
                          <Link
                            to={`/admin/app/${app.app_id}/detail`}
                            className="text-reset text-underline p-0"
                          >
                            {app.app_nickname}
                          </Link>
                        </td>
                        <td className="border-end">
                          <Link
                            to={`/admin/app/${app.app_id}/detail`}
                            className="text-reset text-underline p-0"
                          >
                            <span
                              className="w-100 d-block text-break pe-3 text-start"
                              style={{ maxWidth: '160px' }}
                            >
                              {app.app_handle}
                            </span>
                          </Link>
                        </td>
                        <td>{formatDateAndTime(app.added)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </div>
        </AccordionItem>
      </Accordion>

      {endUser && endUser.allIdentities && endUser.allIdentities.length !== 0 && (
        <>
          <h4 className="mb-3">{t('admin.endUsers.profile.headers.identities')}</h4>
          <Card className="mb-5 border overflow-hidden">
            <Table responsive>
              <thead>
                <tr>
                  <th className="border-end">{t('common:labels.idType')}</th>
                  <th className="border-end">{t('common:labels.idMaskedValue')}</th>
                  <th className="border-end">{t('common:labels.added')}</th>
                  <th>{t('common:labels.modified')}</th>
                </tr>
              </thead>
              <tbody>
                {endUser.allIdentities.map((id) => (
                  <tr key={id.added_epoch}>
                    <td className="border-end">{id.identity_type}</td>
                    <td className="border-end">{id.identity}</td>
                    <td className="border-end">{formatDateAndTime(id.added)}</td>
                    <td>{formatDateAndTime(id.modified)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      {confirm.show && (
        <ConfirmationModal
          show={confirm.show}
          data={confirm.data}
          title={t('admin.user.update.title')}
          renderMessageComponent={() => (
            <>
              <p className="text-lg mb-4">
                {t(`admin.user.update.confirm`, { name: confirm.data.full_name })}
              </p>
              <p className="text-info">
                {t(`admin.user.update.description`, {
                  name: confirm.data.full_name,
                  handle: confirm.data.handle,
                })}
              </p>
            </>
          )}
          buttonLabel={t('admin.user.update.action')}
          handleSubmit={updateUser}
          setShow={cancelUpdate}
        />
      )}

      {showMigrationModal && (
        <EndUserPassportMigrationModal
          show={showMigrationModal}
          setShow={setShowMigrationModal}
          handle={endUser.handle}
          statusOnly={statusOnly}
          toggleStatusOnly={() => setStatusOnly(!statusOnly)}
          getEndUser={getEndUser}
          userRegisteredAtPriority={endUser.migrated_to_priority || endUser.onboarded_at_priority}
        />
      )}
    </>
  );
}

EndUserProfile.propTypes = {
  /**
   * The values of the form
   */
  // eslint-disable-next-line react/forbid-prop-types
  endUser: PropTypes.object.isRequired,
  /**
   * The function to get end user data
   */
  getEndUser: PropTypes.func.isRequired,
};

export default EndUserProfile;
